<script setup lang="ts">
import type { ClientCustomDashboardInputGraphql } from '~/types/graphql-backend-types/gql-types'
import { useLDFlag } from 'launchdarkly-vue-client-sdk'
import {
  ArrowRightIcon,
  FileIcon,
  InfoIcon,
} from 'lucide-vue-next'
import { useDashboard } from '~/composables/dashboard/useDashboard'
import blurredDashboard from '/assets/blurred_dashboard.png'
import blurredDashboardDark from '/assets/blurred_dashboard_dark.png'

const { t } = useI18n()
const router = useRouter()

const dashboardPayload = computed(() => {
  return { dark: isDark.value }
}) as ComputedRef<ClientCustomDashboardInputGraphql>

const { data: iframeDashboardUrl, isPending } = useDashboard(dashboardPayload)

const {
  user,
  isRecycler,
  isAdmin,
  isProducer,
} = storeToRefs(useAuthStore())

const { data: userOnboardingData, isLoading: userOnboardingLoading } = useCurrentUserOnboarding(true)

// When onboarding is done, we don't need to show the first steps
const showFirstSteps = computed(() => featureFlags.onboarding.value && !userOnboardingLoading.value && !userOnboardingData.value?.onboardingDone)
const firstStepsLoading = computed(() => userOnboardingLoading.value && featureFlags.onboarding.value)
const onboardingCompletion = computed(() => (userOnboardingData.value?.onboarding.onboardingItems?.filter(item => item.done).length ?? 0) / (userOnboardingData.value?.onboarding.onboardingItems?.length ?? 0) * 100)
// Feature flag
const welcomeScreenFeatureFlag = 'welcome-screen'
const welcomeScreenEnabled = useLDFlag(welcomeScreenFeatureFlag, false)

const dashboardCTALabel = computed(() => {
  if (isProducer.value) {
    return user.value.client.hasCollects ? t('dashboard.wp_waiting_for_recycler') : t('dashboard.wp_first_collect')
  }
  else if (isRecycler.value) {
    return t('dashboard.wr_view_collects')
  }
  return t('dashboard.wr_view_collects')
})

const firstStepCards = computed(() => {
  return {
    key: 'first_steps.first_steps_cinqo',
    done: false,
    value: '1',
    icon: FileIcon,
  }
})

onMounted(async () => {
  if (!welcomeScreenEnabled.value)
    router.push('/collects')
})
</script>

<template>
  <div v-if="welcomeScreenEnabled" class="grow flex flex-col">
    <PageHeader
      :title="t('global.welome_screen', { name: user.user.firstName })"
      :subtitle="t('global.welcome_screen_description')"
      :show-back="false"
    />

    <div class="flex grow flex-col gap-15">
      <div v-if="isProducer && !isAdmin">
        <div v-if="firstStepsLoading">
          <div class="skeleton h-48 w-full" />
        </div>

        <FirstStepsCard
          v-if="showFirstSteps && !userOnboardingData?.onboarding.done"
          :key="firstStepCards.key"
          :done="firstStepCards.done"
          :translation-key="firstStepCards.key"
          :value="(firstStepCards.value as string)"
        >
          <template #title>
            <div class="flex gap-2 items-center">
              {{ t(`${firstStepCards.key}.title`) }}

              <Badge color="warning">
                <ArrowRightIcon :size="12" />
                {{ t('first_steps.to_complete') }}
              </Badge>
            </div>
          </template>

          <!-- Document card has a its own right content -->
          <template #rightContent>
            <div
              class="absolute right-5 top-1/2 -translate-y-1/2 radial-progress text-xs border-8 bg-[#5b38db] border-[#5b38db]"
              :style="`--value:${onboardingCompletion}; --size:6rem; --thickness: 6px;`"
              role="progressbar"
            >
              <div class="flex flex-col items-center justify-center">
                <span class="font-semibold text-lg">
                  {{ onboardingCompletion }}%
                </span>
                <span>{{ t('first_steps.completed') }}</span>
              </div>
            </div>
          </template>
        </FirstStepsCard>
      </div>

      <div class="flex flex-col gap-4">
        <RecyclerPendingResponseCard v-if="isRecycler && !isAdmin" />
        <BSDWaitingToBeSignedCard v-if="!isAdmin" />
      </div>

      <div v-if="!isAdmin" class="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <IncomingCollectsCard />
        <ComplianceScoreCard v-if="isProducer" />
        <CollectWithMissingFieldsCard v-if="isRecycler" />
        <AccountManager class="!w-full" />
      </div>

      <div v-if="isPending" class="flex grow flex-col gap-4">
        <div class="skeleton h-40 w-full" />
        <div class="skeleton h-full w-full" />
      </div>

      <div
        v-else
        id="dashboard"
        class="full-page-container bg-base-200"
      >
        <div v-if="(user.client.hasCollects || isAdmin) && iframeDashboardUrl" class="h-full w-full">
          <iframe
            :src="iframeDashboardUrl"
            frameborder="0"
            width="100%"
            height="100%"
            class="min-h-[1000px]"
            allowtransparency
          />
        </div>
        <div v-else class="h-full w-full flex flex-col gap-4">
          <Card>
            <template #title>
              <div class="flex flex-col">
                <div class="flex gap-2">
                  <InfoIcon :size="24" class="text-base-content" />
                  <h2>{{ t("global.dashboard_available_after_first_collect_title") }}</h2>
                </div>
                <p class="text-sm font-normal text-base-content">
                  {{ t('global.dashboard_available_after_first_collect_desc') }}
                </p>
              </div>

              <div class="ml-auto">
                <EButton
                  class="btn-md btn-primary"
                  @click="router.push('/collects')"
                >
                  {{ dashboardCTALabel }}
                </EButton>
              </div>
            </template>
            <template #content>
              <div class="w-full h-60 pt-4">
                <img
                  :src="isDark ? blurredDashboardDark : blurredDashboard"
                  alt="dashboard_preview"
                  class="rounded-xl w-full h-full object-cover"
                >
              </div>
            </template>
          </Card>
        </div>
      </div>

      <div v-if="isProducer" class="flex flex-col gap-7">
        <div class="flex flex-col gap-2">
          <Title :title="t('global.start_infos')" />
          <LightSubtitle :subtitle="t('global.start_infos_desc')" />
        </div>

        <iframe
          class="drop-shadow-lg rounded-xl"
          src="https://common-seal-588.notion.site/ebd/1d21fa984251805faf32e682e80a0d5f"
          width="100%"
          height="600px"
          frameborder="0"
          allowfullscreen
        />
      </div>
    </div>
  </div>
</template>

<route lang="yaml">
meta:
  layout: home
  requiresAuth: true
</route>
